import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BaseLayout from './BaseLayout';
import { buildApiUrl } from '../config/api';
import {
  EMPTY_RESET,
  resetErrorMessage,
  tokenFromSearch,
  validateResetForm,
} from './resetForm';
import '../styles/Login.css';

/**
 * Choose a new password, using the token from the emailed link.
 *
 * On success it navigates to /login carrying a notice, which Login already
 * knows how to render — the same handover Register uses after sign-up. Landing
 * on a bare sign-in form with no confirmation is how that flow used to end, and
 * it read as though nothing had happened.
 */
const ResetPassword = () => {
  const navigate = useNavigate();
  const token = tokenFromSearch(useLocation().search);
  const [form, setForm] = useState(EMPTY_RESET);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors({});
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const found = validateResetForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setIsSubmitting(true);
    setError('');
    try {
      await axios.post(buildApiUrl('/auth/password-reset/confirm'), {
        token,
        password: form.password,
      });
      navigate('/login', {
        replace: true,
        state: { notice: 'Your password has been reset. Sign in with your new password.' },
      });
    } catch (err) {
      setError(resetErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // No token means the link was truncated or hand-typed. Rendering the form
  // would collect a password and then fail on submit, which wastes the user's
  // time and looks like their new password was rejected.
  if (!token) {
    return (
      <BaseLayout>
        <div className="login">
          <div className="card login-card">
            <header className="login-header">
              <span className="pill">Reset password</span>
              <h1 className="page-title login-title">This link is incomplete</h1>
            </header>
            <div className="status-error" role="alert">
              That reset link is missing its token. Request a new one.
            </div>
            <div className="login-actions">
              <Link to="/forgot-password" className="btn-primary">
                Request a new link
              </Link>
            </div>
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout>
      <div className="login">
        <div className="card login-card">
          <header className="login-header">
            <span className="pill">Reset password</span>
            <h1 className="page-title login-title">Choose a new password</h1>
            <p className="meta-copy login-subtitle">
              This link works once. Pick something you have not used here before.
            </p>
          </header>

          {error && (
            <div className="status-error" role="alert">
              {error}
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="reset-password">New password</label>
              <input
                id="reset-password"
                className="field"
                type="password"
                name="password"
                autoComplete="new-password"
                value={form.password}
                onChange={handleChange}
                required
                aria-invalid={errors.password ? 'true' : undefined}
                aria-describedby={errors.password ? 'reset-password-error' : undefined}
              />
              {errors.password && (
                <p className="status-error" id="reset-password-error" role="alert">
                  {errors.password}
                </p>
              )}
            </div>

            <div className="login-field">
              <label htmlFor="reset-confirm">Repeat new password</label>
              <input
                id="reset-confirm"
                className="field"
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={handleChange}
                required
                aria-invalid={errors.confirmPassword ? 'true' : undefined}
                aria-describedby={errors.confirmPassword ? 'reset-confirm-error' : undefined}
              />
              {errors.confirmPassword && (
                <p className="status-error" id="reset-confirm-error" role="alert">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            <div className="login-actions">
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Saving…' : 'Set new password'}
              </button>
              <Link to="/login" className="btn-ghost login-register">
                Back to sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </BaseLayout>
  );
};

export default ResetPassword;
